//ICS-208
import React from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { Input } from '@/components/ui/input';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FemaSectionShell from 'components/IAP/femaCards/FemaSectionShell';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useSearchParams } from 'react-router';

import { filterItemsByOperationalPeriod } from '../../../../../utils/operationalPeriodUtils';
import type { BaseSafetyMessage } from '../../../../../types/iapBaseTypes';
import {
  useCurrentPeriod,
  useIncident,
  useIncidentPermissions,
  usePeriods,
} from '../../../hooks/incidentHooks';
import { OperationalPeriodScheduleControl } from '../OperationalPeriodScheduleControl';
import { useIncidentInfo } from '../../../hooks/incidentInfoHooks';
import { useCreateSafetyMessage } from '../../../hooks/useCreateSafetyMessage';
import { useDeleteSafetyMessage } from '../../../hooks/useDeleteSafetyMessage';
import { useSafetyMessages } from '../../../hooks/useSafetyMessages';
import { useUpdateSafetyMessage } from '../../../hooks/useUpdateSafetyMessage';
import { getIncidentCardTitle } from '../../USCGPage';
import ICSWidget from '../ICSWidget';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import { IcsCheckboxGrid, IcsYesNoRadio } from '../IcsCheckboxGrid';
import Ics208SignatureBox from '../Ics208SignatureBox';
import {
  ICS208_BOX_LABELS,
  ICS208_DATETIME_INITIATED_HINT,
  ICS208_HEALTH_AND_SAFETY_PLAN_HINT,
  ICS208_INCIDENT_LOCATION_HINT,
  ICS208_INCIDENT_NAME_HINT,
  ICS208_KNOWN_HAZARDS_HINT,
  ICS208_OPERATIONAL_PERIOD_HINT,
  ICS208_REQUIRED_PPE_HINT,
  ICS208_SAFETY_OFFICER_HINT,
} from '../ics208BoxLabels';
import SaveableBox from '../SaveableBox';
import SaveableField from '../SaveableField';
import {
  environmentalHazardsOptions,
  otherHazardsOptions,
  physicalHazardsOptions,
  ppeOptions,
} from './safetyPlanOptions';
import {
  fromDateTimeLocalValue,
  fromHealthAndSafetyDraft,
  fromKnownHazardsDraft,
  toDateTimeLocalValue,
  toHealthAndSafetyDraft,
  toKnownHazardsDraft,
  toSafetyMessagePayload,
  type HealthAndSafetyDraft,
  type KnownHazardsDraft,
  type SafetyPlanPatch,
} from './safetyPlanRecord';

const ICS_208_EXPORT_FORMNAME = 'ICS_208_CG';
const ICS_208_EXPORT_ROLES = ['IC/UC', 'PSC', 'Situation Unit Leader'];

/**
 * Site Safety and Health Plan, the ICS 208-CG.
 *
 * Boxes 1 and 2 are the read-only header; boxes 3 to 9 are the safety message's
 * own fields, each saved on its own; box 10 is the Safety Officer's signature.
 *
 * Every box writes the whole safety message record, because the API replaces
 * rather than patches. `toSafetyMessagePayload` is what keeps the boxes a box's
 * save does not show from being blanked.
 */
const SafetyPlan = () => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-safety-plan`;

  const { data: safetyMessages, isLoading, error } = useSafetyMessages();
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const formFamily = useIcsFormFamily();
  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const readOnly = !canEditCards || readOnlyForms;

  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const { data: periods } = usePeriods();
  const currentPeriod = useCurrentPeriod();
  const createSafetyMessage = useCreateSafetyMessage();
  const updateSafetyMessage = useUpdateSafetyMessage();
  const deleteSafetyMessage = useDeleteSafetyMessage();

  const recordVersionSave = useRecordFormVersionSave(
    'ICS_208',
    effectivePeriodNumber
  );
  const recordVersion = React.useCallback(() => {
    if (!readOnlyForms) recordVersionSave.mutate();
  }, [readOnlyForms, recordVersionSave]);

  const safetyMessage = React.useMemo(
    () =>
      filterItemsByOperationalPeriod(
        safetyMessages,
        currentPeriod.currentPeriod,
        periods,
        (message) => message.createdAt
      ),
    [safetyMessages, currentPeriod, periods]
  );

  /**
   * One box's edit, sent as a whole record.
   *
   * A period with no safety message yet gets one created by the first save, so
   * the boxes behave the same whether or not the Add button was used.
   */
  const saveBox = React.useCallback(
    async (patch: SafetyPlanPatch) => {
      const payload: BaseSafetyMessage = toSafetyMessagePayload(
        safetyMessage,
        patch
      );
      if (safetyMessage) {
        await updateSafetyMessage.mutateAsync({
          id: safetyMessage.id,
          // The hook stamps the real incident id over whatever is passed; this
          // blank only satisfies the request type.
          data: { ...payload, incidentId: '' },
        });
      } else {
        await createSafetyMessage.mutateAsync(payload);
      }
      recordVersion();
    },
    [safetyMessage, updateSafetyMessage, createSafetyMessage, recordVersion]
  );

  const handleDelete = async () => {
    if (!safetyMessage) return;
    await deleteSafetyMessage.mutateAsync(safetyMessage.id);
    recordVersion();
  };

  const allowWorkspaceDownload = !!safetyMessage && !error;

  const handleExport = (format: 'pdf' | 'docx') => {
    void exportAsForm(
      {
        formname: ICS_208_EXPORT_FORMNAME,
        data: {},
        mapSketch: null,
        format,
      },
      { allowWorkspaceDownload }
    );
  };

  /**
   * The site health and safety plan question and where the plan lives.
   *
   * Both families print it and both store it in the same `healthAndSafetyInfo`
   * blob, so the editor is written once here: box 9 on the Coast Guard sheet,
   * box 4 on the FEMA one.
   */
  const healthAndSafetyPlanBox = (
    <SaveableBox<HealthAndSafetyDraft>
      id="ics208-health-and-safety-plan"
      label={ICS208_BOX_LABELS.healthAndSafetyPlan}
      disabled={readOnly}
      value={toHealthAndSafetyDraft(safetyMessage)}
      onSave={(draft) =>
        void saveBox({
          healthAndSafetyInfo: fromHealthAndSafetyDraft(draft),
        })
      }
    >
      {(draft, setDraft) => (
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
          <IcsYesNoRadio
            label="Site Health and Safety Plan required"
            testId="ics208-health-and-safety-plan-required"
            value={draft.hasPlan}
            disabled={readOnly}
            onChange={(next) => setDraft({ ...draft, hasPlan: next })}
          />
          <div className="tw:flex tw:flex-col tw:gap-2">
            <span className="tw:text-sm tw:font-medium">
              Health and Safety Plan located at
            </span>
            <Input
              aria-label="Health and Safety Plan located at"
              placeholder="Enter location..."
              disabled={readOnly}
              value={draft.location}
              onChange={(event) =>
                setDraft({ ...draft, location: event.target.value })
              }
            />
          </div>
        </div>
      )}
    </SaveableBox>
  );

  // The FEMA 208 is one Safety Message box titled from the descriptor schema,
  // so it keeps the legacy collapsible shell and the legacy editor.
  if (formFamily === 'fema') {
    return (
      <ConditionalCollapsibleCard
        sectionKey={sectionKey}
        className="planning-widget"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="shield"
            iconColor="primary"
            title={getIncidentCardTitle('safetyPlan')}
            subtitle="Safety message and site safety plan for the operational period"
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            icsRoles={ICS_208_EXPORT_ROLES}
            handleExport={handleExport}
            exportFormname={ICS_208_EXPORT_FORMNAME}
            exportText="Preview ICS-208"
          />
        }
      >
        {/*
         * Box 4's two blanks are one editor writing one record, so it fills
         * the question's slot and the location box is omitted rather than
         * doubled. Both it and the safety message save through `saveBox`,
         * which is what records the version: the descriptor's own save never
         * fires here, since every remaining box is read-only.
         */}
        <FemaSectionShell
          baseId="ICS_208"
          slots={{
            safetyMessage: (
              <SaveableField
                id="ics208-fema-safety-message"
                label="Safety Message"
                multiline
                rows={4}
                placeholder="Enter a description..."
                maxLength={1000}
                disabled={readOnly}
                value={safetyMessage?.safetyMessage ?? ''}
                onSave={(value) => void saveBox({ safetyMessage: value })}
              />
            ),
            siteSafetyPlan: healthAndSafetyPlanBox,
          }}
          omit={['siteSafetyPlanLocation']}
        />
      </ConditionalCollapsibleCard>
    );
  }

  const exportActions = (
    <FormExportButton
      formname={ICS_208_EXPORT_FORMNAME}
      onExport={handleExport}
      label="Preview ICS-208"
      disabled={isExporting}
    />
  );

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_208" />

      {error ? (
        <p className="tw:text-sm tw:text-destructive">
          Error loading safety plans: {error.message}
        </p>
      ) : null}

      <div className="tw:grid tw:min-w-0 tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS208_BOX_LABELS.incidentName}
          testId="ics208-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident name">
              {ICS208_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics208-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS208_BOX_LABELS.incidentLocation}
          testId="ics208-section-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident location">
              {ICS208_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics208-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS208_BOX_LABELS.operationalPeriod}
          testId="ics208-section-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the operational period">
              {ICS208_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics208-field-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS208_BOX_LABELS.datetimeInitiated}
        testId="ics208-section-datetime-initiated"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the date and time initiated">
            {ICS208_DATETIME_INITIATED_HINT}
          </BoxInfo>
        }
      >
        <SaveableField
          id="ics208-datetime-initiated"
          label={ICS208_BOX_LABELS.datetimeInitiated}
          hideLabel
          type="datetime-local"
          disabled={readOnly}
          value={toDateTimeLocalValue(safetyMessage?.datetimeInitiated)}
          onSave={(value) =>
            void saveBox({ datetimeInitiated: fromDateTimeLocalValue(value) })
          }
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208_BOX_LABELS.safetyOfficer}
        testId="ics208-section-safety-officer"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the Safety Officer">
            {ICS208_SAFETY_OFFICER_HINT}
          </BoxInfo>
        }
      >
        <div className="tw:grid tw:min-w-0 tw:gap-3 tw:md:grid-cols-3">
          <SaveableField
            id="ics208-safety-officer"
            label="Safety Officer"
            maxLength={255}
            placeholder="Enter name..."
            disabled={readOnly}
            value={safetyMessage?.safetyOfficer ?? ''}
            onSave={(value) => void saveBox({ safetyOfficer: value })}
          />
          <SaveableField
            id="ics208-duty-phone"
            label="Duty Phone"
            maxLength={255}
            placeholder="Enter phone..."
            disabled={readOnly}
            value={safetyMessage?.dutyPhone ?? ''}
            onSave={(value) => void saveBox({ dutyPhone: value })}
          />
          <SaveableField
            id="ics208-email"
            label="Email"
            maxLength={255}
            placeholder="Enter email..."
            disabled={readOnly}
            value={safetyMessage?.email ?? ''}
            onSave={(value) => void saveBox({ email: value })}
          />
        </div>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208_BOX_LABELS.knownHazards}
        testId="ics208-section-known-hazards"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the known hazards">
            {ICS208_KNOWN_HAZARDS_HINT}
          </BoxInfo>
        }
      >
        <SaveableBox<KnownHazardsDraft>
          id="ics208-known-hazards"
          label={ICS208_BOX_LABELS.knownHazards}
          disabled={readOnly}
          value={toKnownHazardsDraft(safetyMessage)}
          onSave={(draft) =>
            void saveBox({ knownHazards: fromKnownHazardsDraft(draft) })
          }
        >
          {(draft, setDraft) => (
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
              <IcsCheckboxGrid
                label="Physical Hazards"
                testId="ics208-physical-hazards"
                options={physicalHazardsOptions}
                value={draft.physicalHazards}
                disabled={readOnly}
                onChange={(next) =>
                  setDraft({ ...draft, physicalHazards: next })
                }
              />
              <CustomHazardGrid
                label="Environmental Hazards"
                testId="ics208-environmental-hazards"
                options={environmentalHazardsOptions}
                value={draft.environmentalHazards}
                disabled={readOnly}
                onChange={(next) =>
                  setDraft({ ...draft, environmentalHazards: next })
                }
              />
              <CustomHazardGrid
                label="Other Hazards"
                testId="ics208-other-hazards"
                options={otherHazardsOptions}
                value={draft.otherHazards}
                disabled={readOnly}
                onChange={(next) => setDraft({ ...draft, otherHazards: next })}
              />
              <div className="tw:flex tw:flex-col tw:gap-2">
                <span className="tw:text-sm tw:font-medium">Other</span>
                <Input
                  aria-label="Other hazard information"
                  placeholder="Additional hazard information..."
                  disabled={readOnly}
                  value={draft.otherHazardsText}
                  onChange={(event) =>
                    setDraft({ ...draft, otherHazardsText: event.target.value })
                  }
                />
              </div>
            </div>
          )}
        </SaveableBox>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208_BOX_LABELS.safetyMessage}
        testId="ics208-section-safety-message"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics208-safety-message"
          label={ICS208_BOX_LABELS.safetyMessage}
          hideLabel
          multiline
          rows={4}
          maxLength={1000}
          placeholder="Enter a description..."
          disabled={readOnly}
          value={safetyMessage?.safetyMessage ?? ''}
          onSave={(value) => void saveBox({ safetyMessage: value })}
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208_BOX_LABELS.sopAndSafeWorkPractices}
        testId="ics208-section-sop"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics208-sop"
          label={ICS208_BOX_LABELS.sopAndSafeWorkPractices}
          hideLabel
          multiline
          rows={4}
          maxLength={1000}
          placeholder="Enter a description..."
          disabled={readOnly}
          value={safetyMessage?.sopAndSafeWorkPractices ?? ''}
          onSave={(value) => void saveBox({ sopAndSafeWorkPractices: value })}
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208_BOX_LABELS.requiredPpe}
        testId="ics208-section-required-ppe"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the required PPE">
            {ICS208_REQUIRED_PPE_HINT}
          </BoxInfo>
        }
      >
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
          <SaveableBox<string[]>
            id="ics208-required-ppe"
            label={ICS208_BOX_LABELS.requiredPpe}
            disabled={readOnly}
            value={safetyMessage?.requiredPpe?.items ?? []}
            onSave={(items) =>
              void saveBox({
                requiredPpe: items.length > 0 ? { items } : undefined,
              })
            }
          >
            {(draft, setDraft) => (
              <IcsCheckboxGrid
                label={ICS208_BOX_LABELS.requiredPpe}
                hideLabel
                testId="ics208-ppe"
                columns={4}
                options={ppeOptions}
                value={draft}
                disabled={readOnly}
                onChange={setDraft}
              />
            )}
          </SaveableBox>
          <SaveableField
            id="ics208-comments"
            label="Comments"
            multiline
            rows={3}
            maxLength={1000}
            placeholder="Enter a description..."
            disabled={readOnly}
            value={safetyMessage?.comments ?? ''}
            onSave={(value) => void saveBox({ comments: value })}
          />
        </div>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS208_BOX_LABELS.healthAndSafetyPlan}
        testId="ics208-section-health-and-safety-plan"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the Health and Safety Plan">
            {ICS208_HEALTH_AND_SAFETY_PLAN_HINT}
          </BoxInfo>
        }
      >
        {healthAndSafetyPlanBox}
      </Ics204CardSection>

      <Ics208SignatureBox periodNumber={effectivePeriodNumber} />

      {!readOnly && safetyMessage ? (
        <div className="tw:flex tw:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={deleteSafetyMessage.isPending}
            onClick={() => void handleDelete()}
          >
            <Trash2 className="tw:size-4" />
            Delete safety plan
          </Button>
        </div>
      ) : null}
    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return <div className="dark content tw:text-foreground mt-2">{body}</div>;
  }

  return (
    <Card
      data-testid="uscg-208-card"
      data-tour="ics208-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>{getIncidentCardTitle('safetyPlan')}</CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

/**
 * A hazard grid with the blank the printed form reserves for one that is not on
 * it.
 *
 * A typed hazard joins the ticked list, so it prints in the form's custom line
 * and shows as a box of its own next time the card is opened.
 */
const CustomHazardGrid: React.FC<{
  label: string;
  testId: string;
  options: readonly string[];
  value: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}> = ({ label, testId, options, value, disabled, onChange }) => {
  const [custom, setCustom] = React.useState('');

  const addCustom = () => {
    const trimmed = custom.trim();
    if (!trimmed) return;
    if (!value.includes(trimmed)) onChange([...value, trimmed]);
    setCustom('');
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <IcsCheckboxGrid
        label={label}
        testId={testId}
        options={options}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
      <div className="tw:flex tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label={`Add a ${label.toLowerCase()} hazard the form does not list`}
          placeholder={`Add custom ${label.toLowerCase().replace(' hazards', '')} hazard...`}
          disabled={disabled}
          value={custom}
          onChange={(event) => setCustom(event.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || !custom.trim()}
          onClick={addCustom}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

export default SafetyPlan;
